import { useMemo } from 'react';
import { useDRResource } from './queries/drResourceQueries';
import { useDrHistoryLatest } from './queries/drHistoryQueries';
import { usePredictionCblPackData, usePowerMeterDemandPackData, useLivePowerMeterDemandPackData, useLiveCblDataPackData } from './queries/packStatQueries';
import { useSetUpList } from './queries/setUpQueries';
import { usePredictionChargingDemandList } from './queries/predictionDemandQueries';
import dayjs from 'dayjs';
export const useDRResourceDetail = (id) => {
  const { data: drResource, isLoading: drResourceLoading, error: drResourceError, refetch } = useDRResource(id);

  // DR 자원에서 Stations의 SysId 추출
  const stationSysIdList = useMemo(() => {
    if (!drResource?.Stations) return [];
    return drResource.Stations.map(station => station.StationSysId).filter(Boolean);
  }, [drResource?.Stations]);


  const targetDate = dayjs().format('YYYY-MM-DD');

  const { data: setUpData, isLoading: setUpLoading } = useSetUpList();
  const defaultCblModuleName = setUpData?.Items?.find(item => item.Group === 'DefaultCBL' && item.Key === 'DEFAULT_MODULE_NAME')?.Value;
  const defaultCblModuleVersion = setUpData?.Items?.find(item => item.Group === 'DefaultCBL' && item.Key === 'DEFAULT_MODULE_VERSION')?.Value;
  const defaultChargingDemandPredictionModuleName = setUpData?.Items?.find(item => item.Group === 'ChargingDemandAi' && item.Key === 'CHARGING_DEMAND_AI_NAME')?.Value;
  const defaultChargingDemandPredictionModuleVersion = setUpData?.Items?.find(item => item.Group === 'ChargingDemandAi' && item.Key === 'CHARGING_DEMAND_AI_VERSION')?.Value;

  const cblModuleName = drResource?.AppliiedCblAlgorith && drResource?.AppliiedCblAlgorithm !== '' ? drResource?.AppliiedCblAlgorithm : defaultCblModuleName;
  const cblModuleVersion = drResource?.AppliedCblAlgorithmVersion && drResource?.AppliedCblAlgorithmVersion !== '' ? drResource?.AppliedCblAlgorithmVersion : defaultCblModuleVersion;

  // 예측 CBL 조회
  const {
    data: predictionCblData,
    isLoading: predictionCblLoading,
    error: predictionCblError,
    refetch: refetchPredictionCbl
  } = usePredictionCblPackData(
    stationSysIdList, targetDate, cblModuleName, cblModuleVersion
  );

  // 어제 전력 수요 조회
  const {
    data: powerMeterDemandData,
    isLoading: powerMeterDemandLoading,
    error: powerMeterDemandError,
    refetch: refetchPowerMeterDemand
  } = usePowerMeterDemandPackData(stationSysIdList, dayjs(targetDate).subtract(1, 'day').format('YYYY-MM-DD'), cblModuleName, cblModuleVersion);

  // 실시간 전력 수요 조회
  const {
    data: livePowerMeterDemandData,
    isLoading: livePowerMeterDemandLoading,
    error: livePowerMeterDemandError,
    refetch: refetchLivePowerMeterDemand
  } = useLivePowerMeterDemandPackData(stationSysIdList, targetDate);

  // 실시간 CBL 조회
  const {
    data: liveCblData,
    isLoading: liveCblLoading,
    error: liveCblError,
    refetch: refetchLiveCbl
  } = useLiveCblDataPackData(stationSysIdList, targetDate, cblModuleName, cblModuleVersion);

  // 최신 DR 발령 정보 조회
  const {
    data: drHistoryLatestData,
    isLoading: drHistoryLatestLoading,
    error: drHistoryLatestError,
    refetch: refetchDrHistoryLatest
  } = useDrHistoryLatest();

  // dr발령일 시간별 전력수요 조회
  const {
    data: drEventPowerMeterDemandData,
    isLoading: drEventPowerMeterDemandLoading,
    error: drEventPowerMeterDemandError,
    refetch: refetchDrEventPowerMeterDemand
  } = usePowerMeterDemandPackData(stationSysIdList, dayjs(drHistoryLatestData?.date).format('YYYY-MM-DD'));


  // 예측 수요 조회
  const {
    data: predictionChargingDemandData,
    isLoading: predictionChargingDemandLoading,
    error: predictionChargingDemandError,
    refetch: refetchPredictionChargingDemand
  } = usePredictionChargingDemandList({
    FromDate: dayjs().subtract(6, 'day').format('YYYY-MM-DD'),
    ToDate: dayjs().format('YYYY-MM-DD'),
    DrResourceSysId: drResource?.DrResourceSysId,
    AppliedModuleName: defaultChargingDemandPredictionModuleName,
    AppliedModuleVersion: defaultChargingDemandPredictionModuleVersion,
  }, {
    enabled: !!(
      drResource?.DrResourceSysId &&
      defaultChargingDemandPredictionModuleName &&
      defaultChargingDemandPredictionModuleVersion &&
      !setUpLoading &&
      !drResourceLoading
    ),
  });

  // 통합 로딩 상태
  const isLoading = drResourceLoading || predictionCblLoading || powerMeterDemandLoading || livePowerMeterDemandLoading || liveCblLoading || drHistoryLatestLoading || drEventPowerMeterDemandLoading;

  // 통합 에러 상태
  const hasError = drResourceError || predictionCblError || powerMeterDemandError || 
                  livePowerMeterDemandError || liveCblError || drHistoryLatestError || drEventPowerMeterDemandError;

  // 모든 데이터 리페치 함수
  const refetchAll = () => {
    refetch();
    refetchPredictionCbl();
    refetchPowerMeterDemand();
    refetchLivePowerMeterDemand();
    refetchLiveCbl();
    refetchDrHistoryLatest();
    refetchDrEventPowerMeterDemand();
  };

  const getPredictionChargingDemand = ({ FromDate, ToDate, AppliedModuleName, AppliedModuleVersion, Page, PageSize, OrderBy, OrderDirection }) => {
    refetchPredictionChargingDemand({
      FromDate,
      ToDate,
      DrResourceSysId: drResource?.DrResourceSysId,
      AppliedModuleName,
      AppliedModuleVersion,
      Page,
      PageSize,
      OrderBy,
      OrderDirection,
    });
  };

  const getDRResourceDetail = () => {
    if (id) {
      refetchAll();
    }
  };

  return { 
    drResource, 
    isLoading, 
    error: hasError, 
    getDRResourceDetail,
    // 그래프 데이터
    predictionCblData,
    powerMeterDemandData,
    livePowerMeterDemandData,
    liveCblData,
    drEventPowerMeterDemandData,
    // DR 발령 정보
    drHistoryLatestData,
    // 리페치 함수들
    refetchAll,
    // 기본 모듈 데이터
    defaultCblModuleName,
    defaultCblModuleVersion,
    defaultChargingDemandPredictionModuleName,
    defaultChargingDemandPredictionModuleVersion,
    // 예측 수요 데이터
    predictionChargingDemandData,
    predictionChargingDemandLoading,
    predictionChargingDemandError,
    getPredictionChargingDemand,
  };

}